import { useState } from 'react';
import { strings } from '../lib/blog';
import { localePath, type Lang } from '../lib/i18n';
import { LangSwitcher } from './LangSwitcher';
import { SiteNav } from './SiteNav';
import { STORE_IOS } from '../lib/constants';

interface BlogHeaderProps {
  lang: Lang;
}

/** Site-wide floating pill nav (home, blog, exercises, equipment, East vs West). */
export function BlogHeader({ lang }: BlogHeaderProps) {
  const t = strings[lang];
  const homeHref = localePath(lang, '/');
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 px-3 pt-3 md:px-6">
      <div className="mx-auto max-w-6xl rounded-full border-2 border-line bg-surface/90 backdrop-blur-md shadow-[var(--card-shadow)] pl-3 pr-2 sm:pl-4">
        <div className="h-14 md:h-16 flex items-center justify-between gap-3">
          <a href={homeHref} className="group flex items-center gap-2 min-w-0 flex-shrink">
            <img
              src="/logo.png"
              alt="ArmProgress"
              width={36}
              height={36}
              className="w-9 h-9 rounded-xl flex-shrink-0 transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110"
            />
            <span className="font-heading text-ink text-lg font-black tracking-tight truncate">
              ArmProgress<span className="text-primary">.</span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1 flex-shrink-0">
            <SiteNav lang={lang} />
            <LangSwitcher lang={lang} className="ml-2" />
            <a
              href={STORE_IOS}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-3d btn-3d--sm ml-3 px-5 py-2.5"
            >
              {t.cta}
            </a>
          </nav>

          {/* Mobile: hamburger toggle */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            className="xl:hidden btn-3d btn-3d--ghost flex-shrink-0 w-10 h-10 p-0 mr-1"
          >
            {menuOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.6} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div className="xl:hidden pop-in mx-auto max-w-6xl mt-2 card-float border-2 border-line px-4 py-4">
          <div className="flex flex-col gap-1" onClick={() => setMenuOpen(false)}>
            <SiteNav
              lang={lang}
              linkClassName="rounded-2xl px-4 py-3 font-heading text-lg font-black text-ink hover:bg-[var(--brand-soft)] hover:text-[var(--brand-ink)] transition-colors"
            />
          </div>
          <div className="mt-3 pt-4 border-t-2 border-line flex items-center justify-between gap-3">
            <LangSwitcher lang={lang} />
            <a href={STORE_IOS} target="_blank" rel="noopener noreferrer" className="btn-3d btn-3d--sm px-5 py-2.5">
              {t.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
